import { useAtomValue } from 'jotai';
import { useMatch } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { NameBadge } from '../../components/NameBadge';
import { PROVIDER_PATH } from '../../config';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import {
  activelySpeakingParticipantIdsAtom,
  isOwnAudioOffAtom,
  isOwnVideoOffAtom,
  isScreenShareModeAtom,
  normalizeVideoPlatform,
  useRemoteVideoComponent,
} from '../../video-sdk';
import { useRemoteDataComponent } from '../../video-sdk/actions/useComponentActions';
import { remoteParticipantConnectionStatesByIdAtom } from '../../video-sdk/core/room/participant/atoms';
import { ParticipantConnectionState } from '../../video-sdk/core/room/participant/types';
import { remoteMediaStatusByParticipantIdAtom, userDataFamily } from '../../video-sdk/twilio';
import { isWhiteboardModeAtom } from '../messages/state';
import { useRemoteMedia } from '../useRemoteMedia';
import { RemoteParticipantCell } from './RemoteParticipantCell';
import type { NameBadgeWrapperProps, RemoteParticipantWrapperProps } from './RemoteParticipantCell';
import { RemoteParticipantReconnectingOverlayWrapper } from './reconnect/RemoteParticipantReconnectingOverlayWrapper';
import { useTriggerPictureInPicture } from './useTriggerPictureInPicture';

export function NameBadgeWrapper({ className, participant }: NameBadgeWrapperProps) {
  const participantName = participant.displayName;
  const audioStatus = useAtomValue(remoteMediaStatusByParticipantIdAtom)[participant.identity];
  const userData = useAtomValue(userDataFamily(participant.identity));

  return (
    <NameBadge
      audioMuted={audioStatus?.isAudioMuted ?? true}
      name={participantName}
      pronouns={userData?.pronouns}
      className={className}
      isSelf={false}
    />
  );
}

export function RemoteParticipantWrapper({
  remoteParticipant,
  className,
  videoClassName,
  videoStyle,
  ...listeners
}: RemoteParticipantWrapperProps) {
  const { audioRef, videoRef } = useRemoteMedia({
    remoteParticipant,
    ...listeners,
  });
  const isOwnAudioOff = useAtomValue(isOwnAudioOffAtom);
  const isOwnVideoOff = useAtomValue(isOwnVideoOffAtom);
  const remoteMediaStatusByParticipantId = useAtomValue(remoteMediaStatusByParticipantIdAtom);
  const videoDelegatorStatus = remoteMediaStatusByParticipantId[remoteParticipant.identity ?? ''];
  const isVideoOff = videoDelegatorStatus?.isVideoOff ?? true;
  const isScreenShareMode = useAtomValue(isScreenShareModeAtom);
  const isWhiteboardMode = useAtomValue(isWhiteboardModeAtom);

  const isSpeaking = useAtomValue(activelySpeakingParticipantIdsAtom).has(remoteParticipant.identity);
  const isProviderView = !!useMatch(PROVIDER_PATH);
  const connectionState = useAtomValue(remoteParticipantConnectionStatesByIdAtom)[remoteParticipant.identity];
  const isReconnecting = connectionState === ParticipantConnectionState.Reconnecting;
  const RemoteData = useRemoteDataComponent();
  const RemoteVideo = useRemoteVideoComponent();
  const t = useTranslate();
  const videoPlatform = normalizeVideoPlatform(useVideoPlatform());

  useTriggerPictureInPicture(remoteParticipant?.identity ?? '', videoRef);

  return (
    <>
      <RemoteData remoteParticipant={remoteParticipant} {...listeners} />
      <RemoteParticipantCell
        audioRef={audioRef}
        className={className}
        isOwnAudioOff={isOwnAudioOff}
        isOwnVideoOff={isOwnVideoOff}
        isProviderView={isProviderView}
        isScreenShareMode={isScreenShareMode}
        isWhiteboardMode={isWhiteboardMode.value}
        isSpeaking={isSpeaking}
        isVideoOff={isVideoOff}
        nameBadgeComponent={NameBadgeWrapper}
        remoteParticipant={remoteParticipant}
        videoRef={videoRef}
        isReconnecting={isReconnecting}
        remoteParticipantReconnectingOverlayComponent={RemoteParticipantReconnectingOverlayWrapper}
        remoteVideoComponent={RemoteVideo}
        t={t}
        videoClassName={videoClassName}
        videoPlatform={videoPlatform}
        videoStyle={videoStyle}
      />
    </>
  );
}
